import * as uuid from "uuid"
import type { StateObject } from "../../../redux/types"
import {
    getFilters,
    getSelectedLocationIdsFromGlobalFilter,
    getTimezoneForQueryParam,
} from "../../../filters/selectors"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import type {
    SpecialPricingLogReportItem,
    SpecialPricingReportRequestData,
    SpecialPricingSummaryReportItem,
} from "../../../../graphql/types"
import { getReportsStateObject } from "../selectors"
import { recursion } from "../../../../utils/helpers"
import { getMidnightOffset } from "../../settings/selectors"
import { getUtcEndOfDay, getUtcStartOfDay } from "../../../../utils/dateFunctions"

export const getSpecialPricingLogReportQueryApiParam = (state: StateObject): SpecialPricingReportRequestData => {
    const globalFilters = getFilters(state, GLOBAL_FILTER_IDENTIFIER)
    const selectedLocationIds = getSelectedLocationIdsFromGlobalFilter(state)
    const locationIdsParam = selectedLocationIds?.join() ?? null

    const midnightOffset = getMidnightOffset(state)
    const timezone = getTimezoneForQueryParam(state, selectedLocationIds)
    const from = globalFilters.date.from
    const to = globalFilters.date.to
    const startOfDay = getUtcStartOfDay(from, timezone, midnightOffset)
    const endOfDay = getUtcEndOfDay(to, timezone, midnightOffset)
    return {
        from: startOfDay,
        to: endOfDay,
        timezone: getTimezoneForQueryParam(state, selectedLocationIds),
        ...(locationIdsParam
            ? {
                  location_ids: locationIdsParam,
              }
            : null),
    }
}

export const getDataForSpecialPricingLogsTable = (state: StateObject): SpecialPricingLogReportItem[] => {
    const data = getReportsStateObject(state).specialPricingLogs

    if (!data || data.length === 0) {
        return []
    }

    const finalTotal = data.reduce((acc: number, val: SpecialPricingLogReportItem) => acc + val.total, 0)

    return [
        ...data,
        {
            id: "total",
            // @ts-expect-error
            name: "Total",
            total: finalTotal,
        },
    ]
}

export const getDataForSpecialPricingSummaryTable = (state: StateObject): SpecialPricingSummaryReportItem[] => {
    const data = getReportsStateObject(state).specialPricingSummary
    const totalRow = data && data[0] ? data[0] : null
    const children = data && data[0] && data[0].children ? data[0].children : []

    if (!totalRow || children.length === 0) {
        return []
    }

    const result = children.map(
        recursion((callSelf: (...args: Array<any>) => any, record, index, array, level = 0) => {
            return {
                ...record,
                id: uuid.v4(),
                level: level,
                children:
                    record.children && record.children.length > 0
                        ? record.children.map((value, ...args) => callSelf(value, ...args, level + 1, record))
                        : null,
            }
        })
    )
    return [...result, { ...totalRow, children: null, level: 0 }]
}

export const getDataForSpecialPricingByEmployeeTable = (state: StateObject): SpecialPricingSummaryReportItem[] => {
    const data = getReportsStateObject(state).specialPricingByEmployee
    const totalRow = data && data[0] ? data[0] : null
    const children = data && data[0] && data[0].children ? data[0].children : []

    if (!totalRow || children.length === 0) {
        return []
    }

    const result = children.map(
        recursion((callSelf: (...args: Array<any>) => any, record, index, array, level = 0) => {
            return {
                ...record,
                id: uuid.v4(),
                level: level,
                children:
                    record.children && record.children.length > 0
                        ? record.children.map((value, ...args) => callSelf(value, ...args, level + 1, record))
                        : null,
            }
        })
    )
    return [...result, { ...totalRow, children: null, level: 0 }]
}
